
import { useCore } from '@/composables/core/index.js'
import { getExternalId } from '@/lib/analytics/helpers.js'
import { FEATURE_FLAGS } from '@/lib/consts.js'
import {
  EXTOLE_ELEMENT_ID,
  EXTOLE_ZONE_NAME,
  EXTOLE_CONTENT_ZONE_NAME,
  EXTOLE_CONTAINER,
} from './consts.js'
import { computed } from 'vue'
import {
  useReadyTrigger,
  useLogger,
} from '@booksy-com/vue-tools'
import axios from 'axios'
import { useEppo } from '@/composables/eppo/index.js'

const logger = useLogger('extole')
const readyTrigger = useReadyTrigger()

const isExtoleB2BReferralsEnabled = computed(() => {
  const { store } = useCore()
  const { ff } = useEppo()

  return !store.state.routingConfig.config.b2b_referral
    && ff(FEATURE_FLAGS.Feature_ExtoleB2BReferralsIntegration)
})

const getExtoleContent = async () => {
  await _initialize()
  return axios({
    method: 'POST',
    url: 'https://refer.booksy.com/api/v6/zones',
    headers: {
      Authorization: `Bearer ${window.extole.tokenStore.get()}`,
      'Content-Type': 'application/json',
    },
    data: {
      'event_name': EXTOLE_CONTENT_ZONE_NAME,
      data: _getExtoleData(),
    },
  })
}

const _initialize = async () => {
  if (isExtoleB2BReferralsEnabled.value) {
    try {
      await _initializeExtoleScripts()
    } catch (e) {
      logger.error(e)
    }
    const extoleElement = _appendExtoleElement()

    const data = _getExtoleData()
    logger.debug('identifying to extole with data', data)

    return new Promise((resolve, reject) => {
      window.extole.createZone({
        name: EXTOLE_ZONE_NAME,
        element_id: EXTOLE_ELEMENT_ID,
        data,
      }, (error, zone) => {
        if (error) {
          reject(error)
        }
        logger.debug('extole initialized successfully')
        resolve(extoleElement)
      })
    })
  }
  logger.debug('skipping extole initialization - conditions not met')
}

const openReferralWindow = async () => {
  const extoleElement = await _initialize()
  if (extoleElement) {
    logger.debug('opening extole window')
    extoleElement.click()
  }
}

const logout = async () => {
  if (window.extole?.require && isExtoleB2BReferralsEnabled.value) {
    window.extole.require(['core-root:///common/user-service.js'], function (userService) {
      logger.debug('logging out from extole')
      userService.logout()
    })
  }
}

const _getExtoleData = () => {
  const { store } = useCore()
  const { email, first_name, last_name, id } = store.state.identity.user
  const routingConfig = store.state.routingConfig.config
  const country = store.getters['app/country']
  const locale = store.getters['app/acceptLanguage']
  const userId = getExternalId({ id, country, routingConfig }) || null
  const isProduction = window.location.hostname === 'booksy.com'
  const labels = isProduction ? EXTOLE_CONTAINER.PROD : EXTOLE_CONTAINER.STAGING
  const region = country.toUpperCase()

  return {
    email,
    first_name,
    last_name,
    locale,
    partner_user_id: userId,
    labels,
    region,
  }
}

const _initializeExtoleScripts = () => {
  if (!window.extole) {
    const extoleScript = document.createElement('script')
    extoleScript.setAttribute('src', 'https://refer.booksy.com/core.js')
    extoleScript.setAttribute('async', true)
    extoleScript.setAttribute('data-testid', 'extole-script')
    extoleScript.onload = () => {
      logger.debug('extole script ready')
      readyTrigger.resolveTrigger()
    }
    document.body.appendChild(extoleScript)

    const extoleZoneScript = document.createElement('script')
    extoleZoneScript.setAttribute('type', 'text/javascript')
    extoleZoneScript.setAttribute('data-testid', 'extole-zone-script')
    const innerExtoleZoneScript = `
      (function(c,b,f,k,a){c[b]=c[b]||{};for(c[b].q=c[b].q||[];a<k.length;)f(k[a++],c[b])})(window,"extole",function (c,b){b[c]=b[c]||function (){b.q.push([c,arguments])}},["createZone"],0)
    `
    extoleZoneScript.innerHTML = innerExtoleZoneScript
    document.body.appendChild(extoleZoneScript)
  }

  return readyTrigger.promise
}

const _appendExtoleElement = () => {
  const extoleZoneWebCta = document.getElementById(EXTOLE_ELEMENT_ID)
  if (!extoleZoneWebCta) {
    const extoleElement = document.createElement('span')
    extoleElement.style.cssText = 'display:none;'
    extoleElement.id = EXTOLE_ELEMENT_ID
    document.body.appendChild(extoleElement)
    logger.debug('extole element created')
  }
  return document.getElementById(EXTOLE_ELEMENT_ID)
}

export function useExtole () {
  return {
    isExtoleB2BReferralsEnabled,
    openReferralWindow,
    logout,
    getExtoleContent,
  }
}
